/**
 * [A4] UpgradeSuccessModal
 *
 * Shown after SubscriptionActivationWatcher transitions to "success".
 * Lists all features unlocked by the new subscription in a 2-column grid.
 *
 * Two variants derived from SubscriptionStatus:
 *   paid  — "You're on {tier} 🎉" + "Go to dashboard" / "View receipt"
 *   trial — "Your {N}-day trial is active" + amber expiry badge + same buttons
 *
 * Figma: [A4] UpgradeSuccessModal
 */
import React from "react";
import { SubscriptionStatus } from "../../services/billing.service";
import { TRIAL_DURATION_DAYS } from "../../constants";

// ── Feature display metadata ───────────────────────────────────────────────────

const FEATURE_META: Record<string, { label: string; icon: string; desc: string }> = {
    ai_scribe:              { label: "AI Scribe",                  icon: "🎙️", desc: "Auto-generate SOAP notes from consultation audio." },
    cloud_storage:          { label: "Cloud Storage",              icon: "☁️", desc: "Expanded storage for patient files and documents." },
    whatsapp_notifications: { label: "WhatsApp Notifications",     icon: "💬", desc: "Send reminders and invoices over WhatsApp." },
    advanced_reports:       { label: "Advanced Analytics",         icon: "📊", desc: "Clinic performance and revenue dashboards." },
    abdm_sync:              { label: "ABDM Sync",                  icon: "🔗", desc: "Push patient records to the ABDM health network." },
    cloud_backup:           { label: "Cloud Backup",               icon: "🛡️", desc: "Daily encrypted backups of your clinic data." },
};

function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
    });
}

// ── Component ──────────────────────────────────────────────────────────────────

interface Props {
    status: SubscriptionStatus;
    tierLabel: string;
    onDashboard: () => void;
    onClose: () => void;
}

export const UpgradeSuccessModal: React.FC<Props> = ({
    status,
    tierLabel,
    onDashboard,
    onClose,
}) => {
    const isTrial = status.feature_flags.some((f) => f.is_trial);
    const trialExpiresAt =
        isTrial
            ? (status.feature_flags.find((f) => f.is_trial && f.expires_at)?.expires_at ?? null)
            : null;

    const features = status.features.length > 0 ? status.features : Object.keys(FEATURE_META);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div
                className="bg-white rounded-2xl shadow-2xl max-w-[780px] w-full p-8"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="mb-1">
                    {isTrial ? (
                        <div className="flex flex-wrap items-center gap-3 mb-1">
                            <h2 className="text-2xl font-bold text-slate-900">
                                Your {TRIAL_DURATION_DAYS}-day trial is active
                            </h2>
                            {trialExpiresAt && (
                                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">
                                    Trial — ends {formatDate(trialExpiresAt)}
                                </span>
                            )}
                        </div>
                    ) : (
                        <h2 className="text-2xl font-bold text-slate-900 mb-1">
                            You're on {tierLabel} 🎉
                        </h2>
                    )}
                    <p className="text-sm text-slate-500">
                        {isTrial
                            ? `All premium features are unlocked for your clinic during your trial.`
                            : `All premium features are now unlocked for your clinic.`}
                    </p>
                </div>

                {/* Feature grid */}
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {features.map((key) => {
                        const meta = FEATURE_META[key] ?? { label: key, icon: "⚡", desc: "" };
                        return (
                            <div
                                key={key}
                                className="rounded-xl bg-slate-50 border border-slate-100 p-4"
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xl">{meta.icon}</span>
                                    <span className="text-sm font-semibold text-slate-900">{meta.label}</span>
                                </div>
                                {meta.desc && (
                                    <p className="text-xs text-slate-500 mb-2">{meta.desc}</p>
                                )}
                                <button
                                    onClick={onDashboard}
                                    className="text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
                                >
                                    Try it →
                                </button>
                            </div>
                        );
                    })}
                </div>

                {/* Actions */}
                <div className="mt-6 flex flex-col sm:flex-row items-center gap-3">
                    <button
                        onClick={onDashboard}
                        className="w-full sm:w-auto px-8 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Go to dashboard
                    </button>
                    {status.last_receipt_url && (
                        <a
                            href={status.last_receipt_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full sm:w-auto px-6 py-2.5 text-sm font-semibold text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors text-center"
                        >
                            View receipt
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UpgradeSuccessModal;
